import {
  InternalConcept,
  InternalTag,
  InternalTagImplementations,
  InternalTagOrder,
  InternalTagSaveBody,
  InternalTagTargets,
  InternalTagType,
} from '../types/InternalTag'
import { internalApiserverApi } from './internal-apiserver'

const tagsApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalTags', 'internalConcepts', 'languageMaps'] }).injectEndpoints({
  endpoints: (builder) => ({
    // Names are the stored EN names; JA and ZH live in the `tags` group of the language map.
    getInternalTags: builder.query<InternalTag[], void>({
      query: () => ({ url: 'i/tags/list', params: { type: 'all' } }),
      providesTags: ['internalTags'],
    }),
    getInternalTagImplementations: builder.query<InternalTagImplementations, { marketIso: string }>({
      query: ({ marketIso }) => ({ url: 'i/tags/implementations', params: { marketIso } }),
      providesTags: ['internalTags'],
    }),
    getInternalConcepts: builder.query<InternalConcept[], void>({
      query: () => 'concepts',
      providesTags: ['internalConcepts'],
    }),
    // Creates without a tag `id`, updates with one; answers the stored tag. The backend writes `translations`
    // into the language maps and leaves them alone when it is null.
    // Doesn't invalidate: the page reads the concepts and language maps back first, and a refetch started by
    // invalidation makes that read resolve right away with the data from before the save.
    // The page calls `invalidateInternalTags` after reading back.
    saveInternalTag: builder.mutation<InternalTag, InternalTagSaveBody>({
      query: (body) => ({ url: 'i/tags', method: body.tag.id ? 'PUT' : 'POST', body }),
    }),
    deleteInternalTag: builder.mutation<void, string>({
      query: (id) => ({ url: 'i/tags', method: 'DELETE', params: { id }, responseHandler: 'text' }),
      invalidatesTags: ['internalTags', 'internalConcepts', 'languageMaps'],
    }),
    saveInternalTagOrder: builder.mutation<void, InternalTagOrder>({
      query: (order) => ({ url: 'i/tags/order', method: 'PUT', body: order, responseHandler: 'text' }),
      invalidatesTags: ['internalTags'],
    }),
    // Both answer 200 with an empty body; only a refetch of the tags shows what was stored. They invalidate only the
    // lists of one tag type (`{ type: 'internalTags', id: tagType }`), not every tag: a `refetch()` while the
    // invalidation's refetch is pending resolves at once with the old tags.
    addInternalTagTargets: builder.mutation<void, { type: InternalTagType; targets: InternalTagTargets }>({
      query: ({ type, targets }) => ({ url: `i/tags/${type}/add`, method: 'POST', body: targets, responseHandler: 'text' }),
      invalidatesTags: (_result, _error, { type }) => [{ type: 'internalTags', id: type }],
    }),
    removeInternalTagTargets: builder.mutation<void, { type: InternalTagType; targets: InternalTagTargets }>({
      query: ({ type, targets }) => ({ url: `i/tags/${type}/remove`, method: 'POST', body: targets, responseHandler: 'text' }),
      invalidatesTags: (_result, _error, { type }) => [{ type: 'internalTags', id: type }],
    }),
  }),
})

export const {
  useGetInternalTagsQuery,
  useGetInternalTagImplementationsQuery,
  useGetInternalConceptsQuery,
  useSaveInternalTagMutation,
  useDeleteInternalTagMutation,
  useSaveInternalTagOrderMutation,
  useAddInternalTagTargetsMutation,
  useRemoveInternalTagTargetsMutation,
} = tagsApi

export const invalidateInternalTags = () => tagsApi.util.invalidateTags(['internalTags', 'internalConcepts', 'languageMaps'])
